import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useCreateDcRuntime } from '@api/bff/calibrationRuntime/mutations/useCreateDcRuntime';
import { useUpdateDcRuntime } from '@api/bff/calibrationRuntime/mutations/useUpdateDcRuntime';
import { useAppendMeasurement } from '@api/bff/calibrationRuntime/mutations/useAppendMeasurement';
import { useUserStore } from '@stores/userStore';
import type {
  CalibrationRuntimeData,
  CalibrationRuntimeMutationResult
} from './types';

type UseCalibrationRuntimeMutationsParams = {
  gagesGlobalId: string;
  woDetailGlobalId: string;
  moduleType?: string;
};

const createInitialCalibrationData = (
  partialData?: Partial<CalibrationRuntimeData>
): CalibrationRuntimeData => {
  const now = new Date().toISOString();

  const equipmentCapacity = partialData?.equipment?.capacity || 0;
  const equipmentUnit = partialData?.equipment?.unit || '';
  const equipmentReadability = partialData?.equipment?.readability || 0;
  const equipmentResolution = partialData?.equipment?.resolution || 0;

  return {
    schemaVersion: 1,
    status: {
      calibration: partialData?.status?.calibration || 'not_started',
      workOrder: partialData?.status?.workOrder || 'incomplete',
      event: partialData?.status?.event || 'incomplete',
      sync: partialData?.status?.sync || 'pending',
      lastModified: partialData?.status?.lastModified || now,
      createdAt: partialData?.status?.createdAt || now,
      syncedAt: partialData?.status?.syncedAt ?? null
    },
    equipment: {
      capacity: equipmentCapacity,
      unit: equipmentUnit,
      readability: equipmentReadability,
      resolution: equipmentResolution
    },
    environmental: {
      temperature: partialData?.environmental?.temperature || '',
      temperatureUnit: partialData?.environmental?.temperatureUnit || '°F',
      humidity: partialData?.environmental?.humidity || '',
      pressure: partialData?.environmental?.pressure || ''
    },
    tests: {
      basicReading: partialData?.tests?.basicReading || {
        status: 'NotStarted',
        startedAt: null,
        completedAt: null,
        testPoints: []
      }
    },
    assessment: {
      conditionAsFound: partialData?.assessment?.conditionAsFound,
      conditionAsLeft: partialData?.assessment?.conditionAsLeft,
      operabilityAsFound: partialData?.assessment?.operabilityAsFound,
      operabilityAsLeft: partialData?.assessment?.operabilityAsLeft,
      inspectionNotes: partialData?.assessment?.inspectionNotes
    },
    results: {
      asFoundPassed: partialData?.results?.asFoundPassed || false,
      asLeftPassed: partialData?.results?.asLeftPassed || false,
      completedPoints: partialData?.results?.completedPoints || 0,
      totalPoints: partialData?.results?.totalPoints || 0
    },
    config: {
      testType: partialData?.config?.testType || 'basic-reading',
      parentSpecificationName: partialData?.config?.parentSpecificationName,
      optionalTests: partialData?.config?.optionalTests || [],
      checklistProgress: {
        completedItems:
          partialData?.config?.checklistProgress?.completedItems || [],
        totalItems: partialData?.config?.checklistProgress?.totalItems || 0
      }
    },
    customerSignoff: partialData?.customerSignoff,
    photos: partialData?.photos,
    saveSpecsToEquipment: partialData?.saveSpecsToEquipment || false
  };
};

export const useCalibrationRuntimeMutations = ({
  gagesGlobalId,
  woDetailGlobalId,
  moduleType = 'SCALES'
}: UseCalibrationRuntimeMutationsParams): CalibrationRuntimeMutationResult => {
  const { enqueueSnackbar } = useSnackbar();
  const userName = useUserStore(state => state.userName);

  const {
    mutateAsync: createDcRuntimeApi,
    isPending: isCreatingCalibration,
    error: createError
  } = useCreateDcRuntime();

  const {
    mutateAsync: updateDcRuntimeApi,
    isPending: isUpdatingCalibration,
    error: updateError
  } = useUpdateDcRuntime();

  const { mutateAsync: appendMeasurementApi } = useAppendMeasurement();

  const createCalibrationRuntime = useCallback(
    async (initialData?: Partial<CalibrationRuntimeData>) => {
      if (!gagesGlobalId) {
        enqueueSnackbar('Missing equipment information', { variant: 'error' });
        return null;
      }

      try {
        const calibrationData = createInitialCalibrationData(initialData);
        const jsonData = JSON.stringify(calibrationData);

        const newCalibration = await createDcRuntimeApi({
          gagesGlobalId,
          moduleType,
          woDetailGlobalId: woDetailGlobalId || null,
          lastModifiedBy: userName || 'SYSTEM',
          eventNum: null,
          jsonData
        });

        return newCalibration;
      } catch (error: any) {
        if (
          error?.response?.status === 409 ||
          (error?.response?.data?.message || error?.message || '')
            .toLowerCase()
            .includes('duplicate')
        ) {
          console.log('Calibration record already exists, treating as success');
          return null;
        }

        enqueueSnackbar('Failed to create calibration record', {
          variant: 'error'
        });
        console.error('Failed to create calibration:', error);
        throw error;
      }
    },
    [
      gagesGlobalId,
      woDetailGlobalId,
      moduleType,
      userName,
      createDcRuntimeApi,
      enqueueSnackbar
    ]
  );

  const updateCalibrationRuntime = useCallback(
    async (
      id: string,
      data: CalibrationRuntimeData,
      currentEventNum?: number | null
    ) => {
      if (!id) {
        enqueueSnackbar('Missing calibration ID', { variant: 'error' });
        return null;
      }

      if (!gagesGlobalId) {
        console.error('Missing required fields for update:', {
          gagesGlobalId
        });
        enqueueSnackbar('Missing required information for update', {
          variant: 'error'
        });
        return null;
      }

      try {
        const updatedData = {
          ...data,
          status: {
            ...data.status,
            lastModified: new Date().toISOString(),
            sync: 'synced' as const
          }
        };

        const jsonData = JSON.stringify(updatedData);

        const dto: any = {
          gagesGlobalId,
          moduleType,
          woDetailGlobalId: woDetailGlobalId || null,
          jsonData,
          lastModifiedBy: userName || 'SYSTEM',
          eventNum: currentEventNum ?? null
        };

        const updatedCalibration = await updateDcRuntimeApi({
          id,
          dto
        });

        return updatedCalibration;
      } catch (error: any) {
        if (error?.response?.status === 409) {
          console.warn('Concurrent edit conflict, last write wins');
          return null;
        }

        console.error('Failed to update calibration:', error);
        throw error;
      }
    },
    [
      gagesGlobalId,
      woDetailGlobalId,
      moduleType,
      userName,
      updateDcRuntimeApi,
      enqueueSnackbar
    ]
  );

  const overrideMeasurement = useCallback(
    async (
      id: string,
      pointId: string,
      field: 'asFound' | 'asLeft',
      value: number
    ) => {
      if (!id) {
        enqueueSnackbar('Missing calibration ID', { variant: 'error' });
        return null;
      }

      try {
        return await appendMeasurementApi({
          id,
          dto: {
            targetField: field,
            value,
            testPointId: pointId,
            overridden: true,
            lastModifiedBy: userName || 'SYSTEM'
          }
        });
      } catch (error) {
        enqueueSnackbar('Failed to save measurement override', {
          variant: 'error'
        });
        console.error('Failed to save measurement override:', error);
        throw error;
      }
    },
    [userName, appendMeasurementApi, enqueueSnackbar]
  );

  return {
    createCalibrationRuntime,
    updateCalibrationRuntime,
    overrideMeasurement,
    isCreatingCalibration,
    isUpdatingCalibration,
    createError: createError as Error | null,
    updateError: updateError as Error | null
  };
};
